import { type FC, useMemo } from "react";
import ReactFlow, {
  Background,
  Controls,
  type Edge,
  MarkerType,
  type Node,
} from "reactflow";
import "reactflow/dist/style.css";
import type {
  TopologyNode as TopologyNodeType,
  TopologyRelationship,
} from "../../hooks/useRepositoryTopology";

import { EmptyState } from "../common/EmptyState";
import { GraphContainer } from "../common/GraphContainer";
import { LoadingState } from "../common/LoadingState";
import { SectionHeader } from "../common/SectionHeader";
import {
  type TopologyNodeData,
  topologyNodeTypes,
} from "../nodes/TopologyNodes";
import "../nodes/flowNodes.css";

interface RepositoryTopologyGraphProps {
  nodes: TopologyNodeType[];
  relationships: TopologyRelationship[];
  loading: boolean;
  error: string | null;
}

const X_SPACING = 280;
const Y_SPACING = 140;

function buildLevelMap(
  nodes: TopologyNodeType[],
  relationships: TopologyRelationship[],
): Map<string, number> {
  const levels = new Map<string, number>();
  const slugSet = new Set(nodes.map((n) => n.slug));

  // Build adjacency: source depends on target, so target is at a lower level
  const dependsOn = new Map<string, string[]>();
  for (const rel of relationships) {
    if (!slugSet.has(rel.source_repository_slug)) {
      continue;
    }
    if (!slugSet.has(rel.target_repository_slug)) {
      continue;
    }
    const deps = dependsOn.get(rel.source_repository_slug);
    if (deps) {
      deps.push(rel.target_repository_slug);
    } else {
      dependsOn.set(rel.source_repository_slug, [rel.target_repository_slug]);
    }
  }

  function getLevel(slug: string, visited: Set<string>): number {
    const cached = levels.get(slug);
    if (cached !== undefined) {
      return cached;
    }
    if (visited.has(slug)) {
      return 0;
    }

    visited.add(slug);
    const deps = dependsOn.get(slug);
    if (!deps || deps.length === 0) {
      levels.set(slug, 0);
      return 0;
    }

    let maxDepLevel = -1;
    for (const dep of deps) {
      maxDepLevel = Math.max(maxDepLevel, getLevel(dep, visited));
    }

    const level = maxDepLevel >= 0 ? maxDepLevel + 1 : 0;
    levels.set(slug, level);
    return level;
  }

  for (const node of nodes) {
    getLevel(node.slug, new Set<string>());
  }
  return levels;
}

function layoutTopology(
  nodes: TopologyNodeType[],
  relationships: TopologyRelationship[],
): { nodes: Node<TopologyNodeData>[]; edges: Edge[] } {
  const levels = buildLevelMap(nodes, relationships);

  // Count dependencies and dependents per slug
  const dependencyCount = new Map<string, number>();
  const dependentCount = new Map<string, number>();
  for (const rel of relationships) {
    dependencyCount.set(
      rel.source_repository_slug,
      (dependencyCount.get(rel.source_repository_slug) ?? 0) + 1,
    );
    dependentCount.set(
      rel.target_repository_slug,
      (dependentCount.get(rel.target_repository_slug) ?? 0) + 1,
    );
  }

  // Group by level
  const byLevel = new Map<number, TopologyNodeType[]>();
  for (const node of nodes) {
    const level = levels.get(node.slug) ?? 0;
    const group = byLevel.get(level);
    if (group) {
      group.push(node);
    } else {
      byLevel.set(level, [node]);
    }
  }

  // Build positioned nodes (left-to-right: level 0 on left)
  const graphNodes: Node<TopologyNodeData>[] = [];
  for (const [level, levelNodes] of byLevel) {
    const colHeight = levelNodes.length * Y_SPACING;
    const startY = -colHeight / 2;
    for (let i = 0; i < levelNodes.length; i++) {
      const node = levelNodes[i];
      graphNodes.push({
        data: {
          dependencyCount: dependencyCount.get(node.slug) ?? 0,
          dependentCount: dependentCount.get(node.slug) ?? 0,
          framework: node.framework,
          primaryLanguage: node.primary_language,
          role: node.role,
          slug: node.slug,
        },
        id: node.slug,
        position: { x: level * X_SPACING, y: startY + i * Y_SPACING },
        type: "topology",
      });
    }
  }

  // Build edges: source depends on target, so arrow from target → source
  // (target provides, source consumes)
  const edges: Edge[] = [];
  const slugSet = new Set(nodes.map((n) => n.slug));
  for (const rel of relationships) {
    if (!slugSet.has(rel.source_repository_slug)) {
      continue;
    }
    if (!slugSet.has(rel.target_repository_slug)) {
      continue;
    }
    const isDashed = rel.confidence < 1;
    edges.push({
      id: `${rel.target_repository_slug}-${rel.source_repository_slug}`,
      markerEnd: { type: MarkerType.ArrowClosed },
      source: rel.target_repository_slug,
      style: {
        stroke: "#9ca3af",
        strokeDasharray: isDashed ? "5,5" : undefined,
        strokeWidth: 2,
      },
      target: rel.source_repository_slug,
      type: "smoothstep",
    });
  }

  return { edges, nodes: graphNodes };
}

function renderContent(
  layout: { nodes: Node<TopologyNodeData>[]; edges: Edge[] },
  loading: boolean,
  error: string | null,
  relationshipCount: number,
) {
  if (loading) {
    return <LoadingState message="Loading topology..." />;
  }
  if (error) {
    return <EmptyState title="Failed to load topology" description={error} />;
  }
  if (relationshipCount === 0) {
    return (
      <EmptyState
        title="No relationships found"
        description="Run repository indexing to discover service dependencies."
      />
    );
  }
  return (
    <GraphContainer height={400}>
      <ReactFlow
        nodes={layout.nodes}
        edges={layout.edges}
        nodeTypes={topologyNodeTypes}
        fitView
        attributionPosition="bottom-right"
        nodesDraggable={false}
        proOptions={{ hideAttribution: true }}
      >
        <Background
          color="currentColor"
          className="text-gray-300 dark:text-slate-700"
          gap={24}
        />
        <Controls className="!border-slate-300 !bg-white !p-1 !shadow-sm dark:!border-slate-700 dark:!bg-slate-800" />
      </ReactFlow>
    </GraphContainer>
  );
}

export const RepositoryTopologyGraph: FC<RepositoryTopologyGraphProps> = ({
  nodes,
  relationships,
  loading,
  error,
}) => {
  const layout = useMemo(
    () => layoutTopology(nodes, relationships),
    [nodes, relationships],
  );

  return (
    <div>
      <SectionHeader
        title="Service Topology"
        badge={nodes.length > 0 ? `${nodes.length}` : undefined}
      />
      {renderContent(layout, loading, error, relationships.length)}
    </div>
  );
};
